import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TriangleDiagram from "../../../../components/diagrams/TriangleDiagram";
import FractionInput from "../../../../components/inputs/FractionInput";

// --- utils (jak wcześniej) ---
function simplifySqrt(n) {
  let outside = 1;
  let inside = n;
  for (let i = 2; i * i <= inside; i++) {
    while (inside % (i * i) === 0) {
      inside /= i * i;
      outside *= i;
    }
  }
  return { outside, inside };
}

function gcd(a, b) {
  while (b) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatSinAsSqrtFraction({ a, b, vertex }) {
  const opposite = vertex === "B" ? b : a;
  const hypotenuse = Math.sqrt(a * a + b * b);

  if (Number.isInteger(hypotenuse)) {
    const divisor = gcd(opposite, hypotenuse);
    const num = opposite / divisor;
    const den = hypotenuse / divisor;
    const latex = den === 1 ? `${num}` : `\\dfrac{${num}}{${den}}`;
    return { latex, value: opposite / hypotenuse };
  }

  const sqrt = simplifySqrt(a * a + b * b);
  const fullNumerator = opposite * sqrt.outside;
  const fullDenominator = a * a + b * b;
  const divisor = gcd(fullNumerator, fullDenominator);
  const num = fullNumerator / divisor;
  const den = fullDenominator / divisor;
  const sqrtLatex = `\\sqrt{${sqrt.inside}}`;
  const latex =
    den === 1
      ? `${num === 1 ? "" : num}${sqrtLatex}`
      : `\\dfrac{${num === 1 ? "" : num}${sqrtLatex}}{${den}}`;

  return {
    latex,
    value: opposite / hypotenuse,
  };
}

// --- Główne API zadania ---
export function generateTask(options = {}) {
  const { withAngles = false } = options;

  let a, b, hypotenuse;
  do {
    a = Math.floor(Math.random() * 8) + 2;
    b = Math.floor(Math.random() * 8) + 2;
    hypotenuse = Math.sqrt(a * a + b * b);
  } while (Number.isInteger(hypotenuse) || a === b); // pomijamy pitagorejskie i równe przyprostokątne

  const vertex = Math.random() < 0.5 ? "B" : "C";
  const baseData = { a, b, vertex };
  const { latex, value } = formatSinAsSqrtFraction(baseData);
  const hypotenuseSquared = a * a + b * b;

  const missing = vertex === "B" ? "b" : "a";
  const opposite = vertex === "B" ? b : a;

  let alphaDeg = null;
  let betaDeg = null;

  if (withAngles) {
    const alphaRad = Math.asin(value); // α z definicji sinusa
    const alpha = (alphaRad * 180) / Math.PI;
    alphaDeg = Math.round(alpha); // do 1°
    betaDeg = 90 - alphaDeg;
  }

  return {
    type: "FindTriangleFromSinTask",
    data: {
      a,
      b,
      vertex,
      sinLatex: latex,
      sinValue: value,
      hypotenuse: Math.sqrt(hypotenuseSquared),
      missing,
      withAngles,
      alphaDeg,
      betaDeg,
    },
    expectedAnswer: { a, b }, // sprawdzamy w checkAnswer
  };
}

export function renderTask({ data }) {
  const { a, b, vertex, sinLatex, withAngles } = data;
  const angle = vertex;

  return (
    <>
      <p>
        Na podstawie rysunku poniżej, oblicz długości przyprostokątnych{" "}
        <InlineMath math="a" /> i <InlineMath math="b" />. Kąt{" "}
        <InlineMath math={'\\alpha'} /> znajduje się w wierzchołku{" "}
        <InlineMath math={vertex} />, a{" "}
        <InlineMath math={`\\sin\\alpha = ${sinLatex}`} />.
      </p>
      {withAngles && (
        <p>
          Dodatkowo podaj miary kątów{" "}
          <InlineMath math={'\\alpha'} /> (zaznaczonym na rysunku) i{" "}
          <InlineMath math={'\\beta'} /> (drugim kątem ostrym tego trójkąta
          prostokątnego) w stopniach, z dokładnością do{" "}
          <InlineMath math={'1^\\circ'} />.
        </p>
      )}
      <TriangleDiagram
        a={a}
        b={b}
        angleAt={angle}
        labelSides={{ a: "a", b: "b" }}
      />
    </>
  );
}


export function renderInput({ data }, userAnswer, onChange, disabled) {
  const ans = userAnswer || {};

  return (
    <div className="flex flex-col gap-4">
      {/* boki */}
      <div className="flex items-center gap-2">
        <InlineMath math="a =" />
        <FractionInput
          value={ans.a || ""}
          onChange={(val) => onChange({ ...ans, a: val })}
          disabled={disabled}
          label="a"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math="b =" />
        <FractionInput
          value={ans.b || ""}
          onChange={(val) => onChange({ ...ans, b: val })}
          disabled={disabled}
          label="b"
        />
      </div>

      {/* kąty – tylko w trybie rozszerzonym */}
      {data.withAngles && (
        <>
          <div className="flex items-center gap-2">
            <InlineMath math="\alpha =" />
            <FractionInput
              value={ans.alpha || ""}
              onChange={(val) => onChange({ ...ans, alpha: val })}
              disabled={disabled}
              label="\alpha"
              suffixLatex="^\circ"
            />
          </div>
          <div className="flex items-center gap-2">
            <InlineMath math="\beta =" />
            <FractionInput
              value={ans.beta || ""}
              onChange={(val) => onChange({ ...ans, beta: val })}
              disabled={disabled}
              label="\beta"
              suffixLatex="^\circ"
            />
          </div>
        </>
      )}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  // brak odpowiedzi
  if (!userAnswer || typeof userAnswer !== "object") {
    if (!data.withAngles) return false;
    return {
      isCorrect: false,
      score: 0,
      maxScore: 2,
      parts: { boki: false, kąty: false },
    };
  }

  const parsedA = userAnswer.a?.parsed ?? null;
  const parsedB = userAnswer.b?.parsed ?? null;

  const okA = parsedA !== null && parsedA === data.a;
  const okB = parsedB !== null && parsedB === data.b;
  const okSides = okA && okB;

  // bez kątów: proste zadanie 1-punktowe (boolean)
  if (!data.withAngles) {
    return okSides;
  }

  const alphaParsed = userAnswer.alpha?.parsed ?? null;
  const betaParsed = userAnswer.beta?.parsed ?? null;

  const alphaOk =
    alphaParsed !== null && Math.round(alphaParsed) === data.alphaDeg;
  const betaOk =
    betaParsed !== null && Math.round(betaParsed) === data.betaDeg;
  const okAngles = alphaOk && betaOk;

  // punktacja: 1 pkt za komplet boków, 1 pkt za oba kąty
  let score = 0;
  if (okSides) score += 1;
  if (okAngles) score += 1;

  return {
    isCorrect: score === 2,
    score,
    maxScore: 2,
    parts: {
      boki: okSides,
      kąty: okAngles,
    },
  };
}

export function renderAnswer({ data }) {
  const { a, b, withAngles, alphaDeg, betaDeg } = data;
  return (
    <div className="space-y-2 mt-2">
      <div>
        <InlineMath math={`a = ${a}`} />
      </div>
      <div>
        <InlineMath math={`b = ${b}`} />
      </div>
      {withAngles && (
        <>
          <div>
            <InlineMath math={`\\alpha \\approx ${alphaDeg}^\\circ`} />
          </div>
          <div>
            <InlineMath math={`\\beta \\approx ${betaDeg}^\\circ`} />
          </div>
        </>
      )}
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    (!userAnswer.a?.raw && !userAnswer.b?.raw)
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const alphaPart =
    userAnswer.alpha?.raw != null
      ? `,\\ \\alpha = ${userAnswer.alpha.raw}^\\circ`
      : "";
  const betaPart =
    userAnswer.beta?.raw != null
      ? `,\\ \\beta = ${userAnswer.beta.raw}^\\circ`
      : "";

  return (
    <InlineMath
      math={`a = ${userAnswer.a?.raw || "?"},\\ b = ${
        userAnswer.b?.raw || "?"
      }${alphaPart}${betaPart}`}
    />
  );
}

export function renderExplanation({ data }) {
  const { vertex, sinLatex, a, b, withAngles, alphaDeg, betaDeg } = data;

  const hypSquared = a * a + b * b;
  const { outside, inside } = simplifySqrt(hypSquared);
  const hypLatex =
    outside === 1
      ? `\\sqrt{${inside}}`
      : `${outside}\\sqrt{${inside}}`;

  const missing = vertex === "B" ? "b" : "a";
  const known = missing === "a" ? b : a;
  const knownLabel = missing === "a" ? "b" : "a";
  const unknown = missing === "a" ? a : b;
  const unknownLabel = missing;

  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Z definicji:
        <BlockMath math={`\\sin\\alpha = \\dfrac{\\text{przyprostokątna~naprzeciw~}\\alpha}{\\text{przeciwprostokątna}}`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Podstawiamy dane:
        <BlockMath
          math={`\\sin\\alpha = ${sinLatex} = \\dfrac{${unknownLabel}}{${hypLatex}}`}
        />
      </div>

      <div>
        <strong>Krok 3:</strong> Rozwiązujemy równanie:
        <BlockMath math={`${unknownLabel} = ${sinLatex}\\cdot ${hypLatex}`} />
        <BlockMath math={`${unknownLabel} = ${unknown}`} />
      </div>

      <div>
        <strong>Krok 4:</strong> Sprawdzamy z twierdzenia Pitagorasa:
        <BlockMath math={`a^2 + b^2 = c^2`} />
        <BlockMath math={`${knownLabel}^2 + ${unknownLabel}^2 = ${hypSquared}`} />
      </div>

      {withAngles && (
        <div>
          <strong>Krok 5:</strong> Liczymy kąty.
          <BlockMath math={`\\sin\\alpha = ${sinLatex}`} />
          <BlockMath math={`\\alpha \\approx ${alphaDeg}^\\circ`} />
          <BlockMath math={`\\beta = 90^\\circ - \\alpha \\approx ${betaDeg}^\\circ`} />
        </div>
      )}

      <div>
        <strong>Odpowiedź końcowa:</strong>
        <BlockMath
          math={
            withAngles
              ? `a = ${a},\\ b = ${b},\\ \\alpha \\approx ${alphaDeg}^\\circ,\\ \\beta \\approx ${betaDeg}^\\circ`
              : `a = ${a},\\ b = ${b}`
          }
        />
      </div>
    </div>
  );
}
